/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1687305600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jun 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "chartmogul",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 0,
          "id": "record-matching"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers"
        }, {
          "value": "Field mapping",
          "depth": 0,
          "id": "field-mapping"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers-1"
        }, {
          "value": "Invoices",
          "depth": 1,
          "id": "invoices"
        }, {
          "value": "Transactions",
          "depth": 1,
          "id": "transactions"
        }, {
          "value": "Delete mode",
          "depth": 0,
          "id": "delete-mode"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers-2"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      strong: "strong",
      a: "a",
      pre: "pre",
      span: "span",
      h4: "h4"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To find your API keys, navigate to your profile and create a private API key."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-apikey-start.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following ChartMogul Objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Customers"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Invoices"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Transactions"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), ", ", _jsx(_components.strong, {
          children: "Update"
        }), ", and ", _jsx(_components.strong, {
          children: "Insert"
        }), " modes for ", _jsx(_components.code, {
          children: "Customers"
        }), " objects and ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode for ", _jsx(_components.code, {
          children: "Invoices"
        }), " and ", _jsx(_components.code, {
          children: "Transactions"
        }), " objects."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": Upsert mode pushes new objects to ChartMogul and updates fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": Update mode updates particular fields on existing objects in ChartMogul."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Insert mode pushes new objects to ChartMogul, and doesn't update the objects as they change in your warehouse."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.h3, {
        id: "customers",
        children: "Customers"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, records can be matched from your source to ChartMogul by ", _jsx(_components.code, {
          children: "External ID"
        }), " or ", _jsx(_components.code, {
          children: "Email"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-customer-externalIdMapping-upsert.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, records can be matched from your source to ChartMogul by ", _jsx(_components.code, {
          children: "External ID"
        }), " or ChartMogul's ", _jsx(_components.code, {
          children: "Customer UUID"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-customer-externalIdMapping-update.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["ChartMogul doesn't consider ", _jsx(_components.code, {
            children: "Email"
          }), " as a unique identifier, so upserting\nrecords will apply to all matching emails."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.h3, {
        id: "customers-1",
        children: "Customers"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to ChartMogul's default and custom attributes. When syncing with ", _jsx(_components.strong, {
          children: "Upsert"
        }), " or ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode, ", _jsx(_components.code, {
          children: "Data Source UUID"
        }), ", ", _jsx(_components.code, {
          children: "Name"
        }), ", and ", _jsx(_components.code, {
          children: "External ID"
        }), "\n(depending on if ", _jsx(_components.code, {
          children: "External ID"
        }), " was selected in the Record Matching section) are required fields in order for the sync to run successfully. When syncing with ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, ", _jsx(_components.code, {
          children: "Name"
        }), "\nis a required field in order for the sync to run successfully. To know more about ", _jsx(_components.code, {
          children: "Customer"
        }), " fields, refer to the ", _jsx(_components.a, {
          href: "https://dev.chartmogul.com/reference/customers",
          children: "ChartMogul docs"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Additionally when syncing custom attributes with ", _jsx(_components.strong, {
          children: "Upsert"
        }), " or ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode, Hightouch automatically transforms your data to match the required\nformat that ChartMogul expects for the ", _jsx(_components.code, {
          children: "custom"
        }), " field. For example, say you want to enrich a customer profile by inserting ", _jsx(_components.code, {
          children: "age"
        }), " as a custom attribute, where the object is:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-JSON",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"attributes\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"custom\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"age\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "25"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-customer-customMapping-insert.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch will automatically detect the ", _jsx(_components.code, {
          children: "type"
        }), " of the record value and transform the object for you to the expected ", _jsx(_components.code, {
          children: "custom"
        }), " field format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-JSON",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"attributes\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"custom\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n              ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " Integer", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n              ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"key\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"age\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n              ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "25"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "invoices",
        children: "Invoices"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to ChartMogul's default and custom attributes. ", _jsx(_components.code, {
          children: "Customer UUID"
        }), ", ", _jsx(_components.code, {
          children: "External ID"
        }), ", ", _jsx(_components.code, {
          children: "Date"
        }), ", ", _jsx(_components.code, {
          children: "Currency"
        }), ", ", _jsx(_components.code, {
          children: "Line Items"
        }), ", and\n", _jsx(_components.code, {
          children: "Customer External ID"
        }), " are required fields in order for the sync to run successfully. To know more about ", _jsx(_components.code, {
          children: "Invoice"
        }), " fields, refer to the ", _jsx(_components.a, {
          href: "https://dev.chartmogul.com/reference/import-customers-invoices",
          children: "ChartMogul docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-invoice-fieldMapping-upsert.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "transactions",
        children: "Transactions"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to ChartMogul's default and custom attributes. ", _jsx(_components.code, {
          children: "Invoice UUID"
        }), ", ", _jsx(_components.code, {
          children: "Date"
        }), ", ", _jsx(_components.code, {
          children: "Type"
        }), ", and ", _jsx(_components.code, {
          children: "Result"
        }), " are required fields in order\nfor the sync to run successfully. To know more about ", _jsx(_components.code, {
          children: "Transaction"
        }), " fields, refer to the ", _jsx(_components.a, {
          href: "https://dev.chartmogul.com/reference/import-invoice-transaction",
          children: "ChartMogul docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-transaction-fieldMapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.h3, {
        id: "customers-2",
        children: "Customers"
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose what Hightouch's behavior is when records leave the query result set. The default is doing nothing, but you can also set Hightouch to delete the ", _jsx(_components.code, {
          children: "Customer"
        }), " record."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-customer-delete.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["ChartMogul doesn't allow custom attributes to be cleared, so Hightouch will\nsync ", _jsx(_components.code, {
            children: "\"null\""
          }), " to the field instead."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", "\n", _jsx(_components.h4, {
        id: "write-econnreset",
        children: "write ECONNRESET"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["You may receive the ", _jsx(_components.code, {
          children: "write ECONNRESET"
        }), " error if the model columns used for ", _jsx(_components.a, {
          href: "#record-matching",
          children: "record matching"
        }), " or ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "mapping"
        }), " don't have an associated ", _jsx(_components.a, {
          href: "/models/data-types-casting#data-types",
          children: "data type"
        }), " in Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-chartmogul-no-data-type.png",
          alt: "Model data types in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To resolve the error, ensure that each model column has the data type ChartMogul expects.\nRefer to the ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "data types and casting docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
